import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useColorScheme } from "../../context/ColorSchemeContext";
import { useSessionTimer } from "../../hooks/useSessionTimer";
import { ThemeBadge } from "../theme/ThemeBadge";
import { SubscriptionBadge } from "../theme/SubscriptionBadge";
import { Button } from "../common/Button";
import { Toggle } from "../common/Toggle";
import styles from "./TopBar.module.css";
import { AccountSwitcher } from "../common/AccountSwitcher";

const LANGUAGES = { en: "EN", es: "ES", fr: "FR" };

export function TopBar({ onMenuToggle }) {
  const { logout, stayLoggedIn, setStayLoggedIn } = useAuth();
  const { isDark, toggle: toggleDark } = useColorScheme();
  const { label: sessionLabel } = useSessionTimer();
  const { i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className={styles.bar}>
      <button className={[styles.iconButton, styles.menuButton].join(" ")} onClick={onMenuToggle} aria-label="Toggle navigation menu" title="Menu: show or hide the navigation sidebar">
        ☰
      </button>

      <a href="/" className={styles.brand} title="Dashboard: back to your home screen"><img src="/favicon.png" alt="" width="28" height="28" /> A Beekeepers Chronicle</a>

      <div className={styles.badges}>
        <ThemeBadge />
        <SubscriptionBadge />
        {/* Beside the tier badges rather than in a menu: it decides which
            apiary everything you create belongs to, so it has to be
            readable before you start, not found afterwards (#310). Renders
            nothing at all unless you actually help at another apiary. */}
        <AccountSwitcher />
      </div>

      <div className={styles.spacer} />

      <div className={styles.sessionInfo} title="Session timer: time left before you are signed out">
        <span aria-hidden="true">⏱</span>
        <span>{sessionLabel}</span>
      </div>

      <div className={styles.stayLoggedIn} title="Stay logged in: keep this device signed in so you are not asked again soon">
        <Toggle checked={stayLoggedIn} onChange={setStayLoggedIn} label="Stay logged in" id="stay-logged-in-topbar" />
      </div>

      {/* id AND name, not just aria-label. The label makes it
          accessible; the id/name is what stops Chrome reporting "a form
          field element has neither an id nor a name attribute" and lets
          the browser treat it as a real field. Reported from dev,
          4 Oct. */}
      <select
        id="language-select"
        name="language"
        aria-label="Language"
        title="Language: change the language the app is shown in"
        className={styles.langSelect}
        value={i18n.language?.slice(0, 2)}
        onChange={(e) => i18n.changeLanguage(e.target.value)}
      >
        {Object.entries(LANGUAGES).map(([code, label]) => (
          <option key={code} value={code}>{label}</option>
        ))}
      </select>

      {/* The only way in to Notifications and Today's Briefing on the web.
          The page existed with nothing linking to it (found 14 Oct). */}
      <a href="/notifications" className={styles.iconButton} aria-label="Notifications and today's briefing" title="Notifications &amp; Today's Briefing: your alerts and what needs doing today">
        🔔
      </a>

      <button className={styles.iconButton} onClick={toggleDark} aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"} title={isDark ? "Light view: switch the app to light colours" : "Dark view: switch the app to dark colours"}>
        {isDark ? "☀️" : "🌙"}
      </button>

      <div className={styles.userMenu}>
        <button className={styles.iconButton} onClick={() => setMenuOpen((v) => !v)} aria-haspopup="true" aria-expanded={menuOpen} aria-label="Account menu" title="Account: Settings, Security Center and Sign out">
          👤
        </button>
        {menuOpen && (
          <div className={styles.dropdown} role="menu">
            <a href="/settings" role="menuitem" className={styles.dropdownItem}>Settings</a>
            <a href="/settings/security" role="menuitem" className={styles.dropdownItem}>Security Center</a>
            <Button variant="ghost" size="sm" onClick={logout} className={styles.dropdownItem}>
              Sign out
            </Button>
          </div>
        )}
      </div>
    </header>
  );
}
